<template>
  <div v-if="visible" class="league-dropdown-menu" @click.stop>
    <!-- 顶部Tab切换 -->
    <div class="league-dropdown-tabs">
      <div 
        class="league-tab-item" 
        :class="{ active: activeTab === 'filter' }"
        @click="activeTab = 'filter'"
      >
        赛事筛选
      </div>
      <div 
        class="league-tab-item" 
        :class="{ active: activeTab === 'country' }"
        @click="activeTab = 'country'"
      >
        国家/地区
      </div>
    </div>
    
    <!-- 内容区域 -->
    <div class="league-dropdown-content">
      <!-- 赛事筛选Tab内容 -->
      <div v-if="activeTab === 'filter'" class="league-filter-content">
        <!-- 按字母分组的联赛 -->
        <div 
          v-for="(leagues, letter) in groupedLeagues" 
          :key="letter"
          class="league-alphabet-group"
        >
          <div class="league-alphabet-title">{{ letter }}</div>
          <div class="league-checkbox-grid">
            <div 
              v-for="league in leagues" 
              :key="league.name" 
              class="league-checkbox-item"
              :class="{ 'is-selected': selectedLeagues.has(league.name) }"
            >
              <input 
                type="checkbox" 
                :id="`league-${league.name}`"
                :checked="selectedLeagues.has(league.name)"
                @change="toggleLeagueSelection(league.name)"
                class="league-checkbox"
              />
              <label 
                :for="`league-${league.name}`"
                class="league-label"
              >
                {{ league.name }}
                <span class="league-count">[{{ league.count }}]</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 国家/地区Tab内容 -->
      <div v-if="activeTab === 'country'" class="league-filter-content">
        <!-- 加载状态 -->
        <div v-if="countryLoading" class="league-empty-state">加载中...</div>
        
        <!-- 无数据状态 -->
        <div v-else-if="!countryData || Object.keys(groupedCountries).length === 0" class="league-empty-state">
          暂无国家数据
        </div>
        
        <!-- 按字母分组的国家列表 -->
        <div v-else>
          <div 
            v-for="(countries, letter) in groupedCountries" 
            :key="letter"
            class="league-alphabet-group"
          >
            <div class="league-alphabet-title">{{ letter }}</div>
            <div class="league-checkbox-grid">
              <div 
                v-for="country in countries" 
                :key="country.country" 
                class="league-checkbox-item"
                :class="{ 'is-selected': selectedCountries.has(country.country) }"
              >
                <input 
                  type="checkbox" 
                  :id="`country-${country.country}`"
                  :checked="selectedCountries.has(country.country)"
                  @change="toggleCountrySelection(country.country)"
                  class="league-checkbox"
                />
                <label 
                  :for="`country-${country.country}`"
                  class="league-label"
                >
                  {{ country.country }}
                  <span class="league-count">[{{ country.matchCount }}]</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部筛选按钮（仅在赛事筛选tab显示） -->
    <div v-if="activeTab === 'filter'" class="league-filter-buttons">
      <FilterDropdown
        :model-value="currentFilter"
        mode="buttons"
        @update:modelValue="handleFilterChange"
      />
    </div>
    
    <!-- 底部状态栏 -->
    <div class="league-dropdown-footer">
      <div class="league-selection-status">
        选中 {{ selectedMatchCount }}/{{ totalMatchCount }}场
      </div>
      <div class="league-footer-buttons">
        <button class="league-footer-btn" @click="handleSelectAll">全选</button>
        <button class="league-footer-btn" @click="handleInvertSelection">反选</button>
        <button class="league-footer-btn league-confirm-btn" @click="confirmSelection">确定</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { pinyin } from 'pinyin-pro'
import FilterDropdown from './FilterDropdown.vue'
import { getFootballCountryRate } from '@/api/match'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  matches: {
    type: Array,
    default: () => []
  },
  currentFilter: {
    type: String,
    default: '完整'
  }
})

const emit = defineEmits(['update:visible', 'confirm', 'filter-change'])

// Tab状态
const activeTab = ref('filter')

// 选中的联赛集合
const selectedLeagues = ref(new Set())

// 国家地区数据状态
const countryData = ref(null)
const countryLoading = ref(false)
const selectedCountries = ref(new Set())

/**
 * 获取国家地区数据
 */
const fetchCountryData = async () => {
  if (countryData.value) {
    console.log('国家数据已缓存，不重复请求')
    return // 已加载过，不重复请求
  }
  
  try {
    countryLoading.value = true
    console.log('开始请求国家地区数据...')
    const response = await getFootballCountryRate()
    console.log('接口响应:', response)
    
    if (response.code === 200 && response.data?.data) {
      countryData.value = response.data.data
      console.log('✅ 国家地区数据加载成功!')
      console.log('  - continent数量:', countryData.value.continent?.length || 0)
      console.log('  - country数量:', countryData.value.country?.length || 0)
      console.log('  - countryLetter keys:', Object.keys(countryData.value.countryLetter || {}))
      console.log('  - 完整数据:', countryData.value)
    } else {
      console.error('❌ 获取国家地区数据失败，响应码:', response.code, response)
    }
  } catch (error) {
    console.error('❌ 获取国家地区数据异常:', error)
  } finally {
    countryLoading.value = false
    console.log('加载状态结束，countryLoading:', countryLoading.value)
  }
}

/**
 * 从比赛列表中提取联赛信息并统计
 */
const extractLeagues = (matches) => {
  const leagueMap = new Map()
  
  if (!matches || !Array.isArray(matches)) {
    return leagueMap
  }
  
  matches.forEach((match) => {
    // 处理可能是 Immutable 对象的情况
    let matchObj = match
    if (match && typeof match.toJS === 'function') {
      matchObj = match.toJS()
    }
    
    const title = matchObj?.title?.trim()
    if (title) {
      leagueMap.set(title, (leagueMap.get(title) || 0) + 1)
    }
  })
  
  return leagueMap
}

/**
 * 按拼音首字母分组联赛
 */
const groupLeaguesByPinyin = (leagueMap) => {
  const groups = {}
  
  leagueMap.forEach((count, name) => {
    // 使用 pinyin-pro 获取首字母
    const firstLetter = pinyin(name, { pattern: 'first', toneType: 'none' })
    const letter = firstLetter && firstLetter[0] ? firstLetter[0].toUpperCase() : '#'
    
    if (!groups[letter]) {
      groups[letter] = []
    }
    
    groups[letter].push({ name, count })
  })
  
  // 按字母排序
  const sortedGroups = {}
  Object.keys(groups).sort().forEach(letter => {
    sortedGroups[letter] = groups[letter]
  })
  
  return sortedGroups
}

/**
 * 计算分组后的联赛列表
 */
const groupedLeagues = computed(() => {
  const leagueMap = extractLeagues(props.matches)
  return groupLeaguesByPinyin(leagueMap)
})

/**
 * 按字母分组的国家列表（用于国家/地区tab）
 */
const groupedCountries = computed(() => {
  if (!countryData.value?.countryLetter) {
    console.log('⚠️ groupedCountries: 无countryLetter数据')
    return {}
  }
  
  // 过滤掉无效的国家数据（没有国家名称的）
  const filteredData = {}
  Object.entries(countryData.value.countryLetter).forEach(([letter, countries]) => {
    if (Array.isArray(countries)) {
      const validCountries = countries.filter(country => 
        country && 
        country.country && 
        typeof country.country === 'string' && 
        country.country.trim() !== ''
      )
      if (validCountries.length > 0) {
        filteredData[letter] = validCountries
      }
    }
  })
  
  console.log('✅ groupedCountries 计算成功，字母数:', Object.keys(filteredData).length)
  return filteredData
})

/**
 * 所有国家列表（扁平化）
 */
const allCountries = computed(() => {
  if (!countryData.value?.country) return []
  return countryData.value.country
})

/**
 * 计算总场数
 */
const totalMatchCount = computed(() => {
  // 根据当前tab计算总场数
  if (activeTab.value === 'country') {
    // 国家筛选模式：所有国家的比赛数总和
    let total = 0
    allCountries.value.forEach(country => {
      total += country.matchCount || 0
    })
    return total
  } else {
    // 联赛筛选模式
    let total = 0
    Object.values(groupedLeagues.value).forEach(leagues => {
      leagues.forEach(league => {
        total += league.count
      })
    })
    return total
  }
})

/**
 * 计算选中的场数
 */
const selectedMatchCount = computed(() => {
  // 根据当前tab计算选中场数
  if (activeTab.value === 'country') {
    // 国家筛选模式：计算选中国家的比赛数
    let total = 0
    allCountries.value.forEach(country => {
      if (selectedCountries.value.has(country.country)) {
        total += country.matchCount || 0
      }
    })
    return total
  } else {
    // 联赛筛选模式
    let total = 0
    Object.values(groupedLeagues.value).forEach(leagues => {
      leagues.forEach(league => {
        if (selectedLeagues.value.has(league.name)) {
          total += league.count
        }
      })
    })
    return total
  }
})

/**
 * 切换联赛选中状态
 */
const toggleLeagueSelection = (leagueName) => {
  if (selectedLeagues.value.has(leagueName)) {
    selectedLeagues.value.delete(leagueName)
  } else {
    selectedLeagues.value.add(leagueName)
  }
  // 触发响应式更新
  selectedLeagues.value = new Set(selectedLeagues.value)
}

/**
 * 切换国家选中状态
 */
const toggleCountrySelection = (countryName) => {
  if (selectedCountries.value.has(countryName)) {
    selectedCountries.value.delete(countryName)
  } else {
    selectedCountries.value.add(countryName)
  }
  // 触发响应式更新
  selectedCountries.value = new Set(selectedCountries.value)
}

/**
 * 全选联赛
 */
const selectAllLeagues = () => {
  const newSet = new Set()
  Object.values(groupedLeagues.value).forEach(leagues => {
    leagues.forEach(league => {
      newSet.add(league.name)
    })
  })
  selectedLeagues.value = newSet
}

/**
 * 全选国家
 */
const selectAllCountries = () => {
  const newSet = new Set()
  allCountries.value.forEach(country => {
    newSet.add(country.country)
  })
  selectedCountries.value = newSet
}

/**
 * 反选联赛
 */
const invertLeagueSelection = () => {
  const allLeagues = new Set()
  Object.values(groupedLeagues.value).forEach(leagues => {
    leagues.forEach(league => {
      allLeagues.add(league.name)
    })
  })
  
  const newSet = new Set()
  allLeagues.forEach(name => {
    if (!selectedLeagues.value.has(name)) {
      newSet.add(name)
    }
  })
  
  selectedLeagues.value = newSet
}

/**
 * 反选国家
 */
const invertCountrySelection = () => {
  const allCountryNames = new Set()
  allCountries.value.forEach(country => {
    allCountryNames.add(country.country)
  })
  
  const newSet = new Set()
  allCountryNames.forEach(name => {
    if (!selectedCountries.value.has(name)) {
      newSet.add(name)
    }
  })
  
  selectedCountries.value = newSet
}

/**
 * 处理筛选选项变化
 */
const handleFilterChange = (option) => {
  emit('filter-change', option)
}

/**
 * 处理全选（根据当前tab）
 */
const handleSelectAll = () => {
  if (activeTab.value === 'country') {
    selectAllCountries()
  } else {
    selectAllLeagues()
  }
}

/**
 * 处理反选（根据当前tab）
 */
const handleInvertSelection = () => {
  if (activeTab.value === 'country') {
    invertCountrySelection()
  } else {
    invertLeagueSelection()
  }
}

/**
 * 确认选择
 */
const confirmSelection = () => {
  if (activeTab.value === 'country') {
    // 国家筛选模式：收集所有选中国家的matchIds
    const matchIdsSet = new Set()
    console.log('🔍 开始收集国家matchIds')
    console.log('  - 选中的国家数量:', selectedCountries.value.size)
    console.log('  - 选中的国家:', Array.from(selectedCountries.value))
    console.log('  - 所有国家数据:', allCountries.value)
    
    allCountries.value.forEach(country => {
      if (selectedCountries.value.has(country.country)) {
        console.log(`  - 处理国家: ${country.country}`)
        console.log(`    matchIds存在:`, !!country.matchIds)
        console.log(`    matchIds是数组:`, Array.isArray(country.matchIds))
        console.log(`    matchIds内容:`, country.matchIds)
        
        // 合并该国家的所有比赛ID
        if (country.matchIds && Array.isArray(country.matchIds)) {
          country.matchIds.forEach(id => matchIdsSet.add(id))
          console.log(`    添加了 ${country.matchIds.length} 个比赛ID`)
        }
      }
    })
    
    const matchIds = Array.from(matchIdsSet)
    console.log('✅ 收集完成，总共matchIds数量:', matchIds.length)
    console.log('✅ matchIds列表:', matchIds)
    emit('confirm', { type: 'country', value: matchIds })
  } else {
    // 联赛筛选模式
    const selectedLeagueNames = Array.from(selectedLeagues.value)
    emit('confirm', { type: 'league', value: selectedLeagueNames })
  }
  
  emit('update:visible', false)
}

/**
 * 监听 matches 变化，初始化选中状态（默认全选）
 */
watch(() => props.matches, (newMatches) => {
  if (newMatches && newMatches.length > 0 && selectedLeagues.value.size === 0) {
    selectAllLeagues()
  }
}, { immediate: true })

/**
 * 监听弹窗显示，切换到国家tab时加载数据
 */
watch(() => props.visible, (newVisible) => {
  if (newVisible && !countryData.value) {
    fetchCountryData()
  }
})

/**
 * 监听tab切换，切换到国家tab时加载数据
 */
watch(activeTab, (newTab) => {
  if (newTab === 'country' && !countryData.value) {
    fetchCountryData()
  }
})
</script>

<style scoped lang="scss">
.league-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  width: 460px;
  max-height: 600px;
  background: $scoreFootball-white;
  border-radius: 8px;
  border: 1px solid $scoreFootball-border-light;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // 顶部Tab切换
  .league-dropdown-tabs {
    display: flex;
    border-bottom: 1px solid $scoreFootball-border-light;
    background: $scoreFootball-white;

    .league-tab-item {
      flex: 1;
      padding: 12px 20px;
      text-align: center;
      font-size: 14px;
      font-weight: 500;
      color: $scoreFootball-text-secondary;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;

      &.active {
        color: $color-primary;
        
        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: $color-primary;
        }
      }

      &:hover {
        color: $color-primary;
      }
    }
  }

  // 内容区域
  .league-dropdown-content {
    padding: 12px 16px;
    overflow-y: auto;
    flex: 1;
    max-height: 450px;

    .league-filter-content {
      padding-bottom: 0;
      margin-bottom: 0;
      display: flex;
      flex-direction: column;

      // 字母分组标题
      .league-alphabet-group {
        margin-top: 20px;
        margin-bottom: 0;

        &:first-child {
          margin-top: 0;
        }

        .league-alphabet-title {
          font-size: 14px;
          font-weight: 600;
          color: $scoreFootball-text-primary;
          margin-bottom: 12px;
        }
      }

      // 复选框网格布局（3列）
      .league-checkbox-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px 16px;
        margin-bottom: 0;

        .league-checkbox-item {
          display: flex;
          align-items: center;
          cursor: pointer;

          .league-checkbox {
            width: 16px;
            height: 16px;
            margin-right: 8px;
            cursor: pointer;
            appearance: none;
            border: 2px solid #d9d9d9;
            border-radius: 50%;
            position: relative;
            transition: all 0.2s ease;

            &:checked {
              background: $color-primary;
              border-color: $color-primary;
              
              &::after {
                content: '✓';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 11px;
                font-weight: bold;
              }
            }

            &:hover {
              border-color: $color-primary;
            }
          }

          .league-label {
            font-size: 13px;
            color: #525a79;
            font-weight: normal;
            cursor: pointer;
            display: flex;
            align-items: center;
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            transition: color 0.2s ease;

            .league-count {
              margin-left: 4px;
              color: #afb8cb;
              font-size: 10px;
              flex-shrink: 0;
            }
          }

          &.is-selected .league-label {
            color: $color-primary;

            .league-count {
              color: #afb8cb;
            }
          }
        }
      }

      .league-empty-state {
        text-align: center;
        padding: 40px 0;
        color: $scoreFootball-text-muted;
        font-size: 14px;
      }
    }
  }

  // 底部筛选按钮容器
  .league-filter-buttons {
    padding: 12px 16px 8px 16px;
    background: $scoreFootball-white;
    z-index: 10;
    margin-top: 0;
    margin-bottom: 0;
  }

  // 底部状态栏
  .league-dropdown-footer {
    padding: 10px 14px;
    border-top: 1px solid $scoreFootball-border-light;
    background: $scoreFootball-white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 11;
    flex-shrink: 0;
    margin-top: 0;

    .league-selection-status {
      font-size: 12px;
      color: $scoreFootball-text-secondary;
    }

    .league-footer-buttons {
      display: flex;
      gap: 6px;

      .league-footer-btn {
        padding: 5px 12px;
        font-size: 12px;
        font-weight: 500;
        color: $scoreFootball-text-secondary;
        background: $scoreFootball-white;
        border: 1px solid $scoreFootball-border-light;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          border-color: $color-primary;
          color: $color-primary;
        }

        &.league-confirm-btn {
          background: $color-primary;
          color: $text-white;
          border-color: $color-primary;

          &:hover {
            background: $color-primary-dark;
            border-color: $color-primary-dark;
          }
        }
      }
    }
  }
}
</style>
